import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { authRoutesNames } from 'src/core/routes/auth/auth.routes.names';
import { coreRoutesNames } from 'src/core/routes/core.routes.names';
import { AuthStore } from 'src/core/stores';

const AUTH_REFRESH_URL = `/${coreRoutesNames.AUTH}/${authRoutesNames.REFRESH}`;
const AUTH_URL = `/${coreRoutesNames.AUTH}/`;

// Shared across interceptor calls to coordinate concurrent refresh requests.
// string = new token available; null = refresh failed; undefined = refresh in progress.
const accessTokenSubject = new BehaviorSubject<string | null | undefined>(
  undefined,
);

const addToken = (
  httpRequest: HttpRequest<unknown>,
  token: string | undefined,
): HttpRequest<unknown> => {
  if (token) {
    return httpRequest.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }
  return httpRequest;
};

const handle401Error = (
  httpRequest: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authStore: InstanceType<typeof AuthStore>,
): Observable<HttpEvent<unknown>> => {
  if (!authStore.isRefreshing()) {
    accessTokenSubject.next(undefined);

    return authStore.refresh().pipe(
      switchMap((tokenResponseDto) => {
        accessTokenSubject.next(tokenResponseDto?.accessToken);
        return next(addToken(httpRequest, tokenResponseDto?.accessToken));
      }),
      catchError((error) => {
        accessTokenSubject.next(null);
        if (error instanceof HttpErrorResponse && error.status === 401) {
          authStore.logout();
        }
        return throwError(() => error);
      }),
    );
  }

  return accessTokenSubject.pipe(
    filter((token) => token !== undefined),
    take(1),
    switchMap((token) => {
      if (token === null) {
        return throwError(() => new Error('Token refresh failed'));
      }
      return next(addToken(httpRequest, token));
    }),
  );
};

export const jwtInterceptor: HttpInterceptorFn = (httpRequest, next) => {
  const authStore = inject(AuthStore);

  let token = authStore.currentAccessToken();
  if (httpRequest.url.includes(AUTH_REFRESH_URL)) {
    token = authStore.currentRefreshToken();
  }

  return next(addToken(httpRequest, token)).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        if (httpRequest.url.includes(AUTH_REFRESH_URL)) {
          authStore.logout();
          return throwError(() => error);
        }
        if (httpRequest.url.includes(AUTH_URL)) {
          return throwError(() => error);
        }
        return handle401Error(httpRequest, next, authStore);
      }
      return throwError(() => error);
    }),
  );
};
